// HTTP client for the Action Center API. Same-origin in production (nginx routes /api ->
// :8317), so no base URL. Cookies (httpOnly session + CSRF) ride automatically with
// credentials:"include"; unsafe methods echo the CSRF token from the non-httpOnly cookie into
// the X-CSRF-Token header (the double-submit contract the API enforces in api/auth.py).

import type {
  Board,
  Comment,
  Page,
  Subtask,
  WorkItem,
  WorkItemCreate,
  WorkItemMove,
  WorkItemUpdate,
  WorkKind,
} from "./types";

const CSRF_COOKIE = "ac_csrf";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]!) : null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // CSRF double-submit for cookie-authenticated unsafe methods.
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  // In dev, a bearer token from the query string / localStorage is accepted so the SPA can be
  // exercised without a full metanoia-auth login. Production uses the httpOnly cookie only.
  const devToken = import.meta.env.DEV ? localStorage.getItem("ac_dev_token") : null;
  if (devToken) headers["Authorization"] = `Bearer ${devToken}`;

  const res = await fetch(`/api/v1${path}`, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const detail = data && typeof data === "object" && "detail" in data ? String(data.detail) : res.statusText;
    throw new ApiError(res.status, detail);
  }
  return data as T;
}

export interface ListParams {
  kind?: WorkKind;
  status?: string;
  assignee_id?: string;
  priority?: string;
  label_id?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

function qs(params: ListParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  listWorkItems: (params: ListParams = {}) => request<Page<WorkItem>>("GET", `/work-items${qs(params)}`),
  tableWorkItems: (params: ListParams = {}) => request<Page<WorkItem>>("GET", `/work-items/table${qs(params)}`),
  board: (kind: WorkKind) => request<Board>("GET", `/boards/${kind}`),
  getWorkItem: (id: string) => request<WorkItem>("GET", `/work-items/${id}`),
  createWorkItem: (body: WorkItemCreate) => request<WorkItem>("POST", "/work-items", body),
  updateWorkItem: (id: string, body: WorkItemUpdate) => request<WorkItem>("PATCH", `/work-items/${id}`, body),
  deleteWorkItem: (id: string) => request<void>("DELETE", `/work-items/${id}`),
  moveWorkItem: (id: string, body: WorkItemMove) => request<WorkItem>("POST", `/work-items/${id}/move`, body),
  listComments: (id: string) => request<Comment[]>("GET", `/work-items/${id}/comments`),
  addComment: (id: string, body: string) => request<Comment>("POST", `/work-items/${id}/comments`, { body }),
  listSubtasks: (id: string) => request<Subtask[]>("GET", `/work-items/${id}/subtasks`),
  addSubtask: (id: string, title: string) => request<Subtask>("POST", `/work-items/${id}/subtasks`, { title }),
  updateSubtask: (sid: string, completed: boolean) =>
    request<Subtask>("PATCH", `/subtasks/${sid}`, { completed }),
};
